import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { observable } from 'mobx';
import PricingParameterPicker from "components/PricingParameterPicker";
import FrequencyPicker from "components/FrequencyPicker";
import ServiceTypeCategoryPicker from 'components/ServiceTypeCategoryPicker';
import { FormGroup, Button, Intent, TextArea, Switch } from "@blueprintjs/core";
import Toast from "helpers/toaster";
import FormDrawer from "components/FormDrawer";
import { FormSection, FieldError, MoreOptions } from "components/FormBits";

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Bring a field with an error into view and put the cursor in it (or on the
// first button of a picker).
const focusField = (id) => {
  setTimeout(() => {
    const element = document.getElementById(id);
    if (!element) return;
    const field = element.matches('input, select, textarea, button') ? element : element.querySelector('button, input');
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (field) field.focus({ preventScroll: true });
  }, 0);
};

// Required fields in the order they appear: key -> field id.
const FIELD_IDS = [
  ['name', 'service-type-name'],
  ['category', 'service-type-category'],
  ['description', 'service-type-description'],
  ['rate', 'service-type-price'],
];

@inject("serviceTypeStore","preferenceStore","pricingParameterStore")
@observer
class AddServiceTypeModal extends Component {
  @observable selectedPricingParameter = null;
  @observable selectedFrequency = null;
  @observable selectedServiceTypeCategory = null;
  @observable errors = {};
  constructor(props) {
    super(props);
    this.state = {
      isChecked: false,
    };
  }

  componentDidMount() {
    let {
      preferenceStore,
      service_type_object,
      service_type_id
    } = this.props;

    if(service_type_id){
      this.setState({
        isChecked: service_type_object.hourly_service,
      });

      this.selectedPricingParameter = service_type_object.service_type_pricing_parameters.map((pps) => {
        return {
          id: pps.id,
          value: pps.pricing_parameter_id,
          selected: true,
          label: pps.label
        }
      })

      this.selectedFrequency = service_type_object.service_frequencies.map((stf) => {
        return {
          id: stf.id,
          value: stf.frequency_id,
          selected: true,
          label: stf.label
        }
      })

      if(service_type_object.service_type_category != null){
        this.selectedServiceTypeCategory = [{
          id: service_type_object.service_type_category.id,
          value: service_type_object.service_type_category.id,
          selected: true,
          label: service_type_object.service_type_category.name
        }]
      }
      
    }

    preferenceStore.loadTenant(); 
    if (this.props.isOpen) {
      this.resetAddForm();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.errors = {};
      this.resetAddForm();
    }
  }

  // Adding: start each open from a blank form with nothing picked, since the
  // dialog stays mounted between opens. (Editing fills from the record.)
  resetAddForm() {
    if (this.props.service_type_id) {
      return;
    }
    this.props.serviceTypeStore.resetNewServiceType();
    this.selectedPricingParameter = null;
    this.selectedFrequency = null;
    this.selectedServiceTypeCategory = null;
    this.setState({ isChecked: false });
  }

  clearError(key) {
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  // Inline errors under each missing field; focus the first one. `fields` is
  // the record being edited (the store object, or its attributes when editing).
  validate(fields) {
    const errors = {};
    if (isBlank(fields.name)) errors.name = 'Enter a name, e.g. Furnace tune-up.';
    if (fields.display_on_booking_form && isBlank(fields.service_type_category_id)) {
      errors.category = 'Choose a category: it’s shown on the booking form.';
    }
    if (isBlank(fields.description)) errors.description = 'Add a short description.';
    if (isBlank(fields.rate)) errors.rate = 'Enter the price, e.g. 129.00.';
    this.errors = errors;
    const first = FIELD_IDS.find(([key]) => errors[key]);
    if (first) {
      focusField(first[1]);
      return false;
    }
    return true;
  }

  createServiceType() {
    let {
      props: {
        serviceTypeStore: {
          newServiceType: {
            object
           }
        },
        preferenceStore: {
          tenant
        }
      }
    } = this;
    if(this.selectedServiceTypeCategory !== null && this.selectedServiceTypeCategory.length > 0 && this.selectedServiceTypeCategory[0] !== null) {
      object.service_type_category_id = this.selectedServiceTypeCategory[0].value;
    }

    if (!this.validate(object)) {
      return;
    }

    object.tenant_id = tenant.object.attributes.id;
    object.hourly_service = this.state.isChecked;
    object.rate_currency = tenant.object.attributes.currency_symbol;
    
    this.props.serviceTypeStore
      .createServiceType()
      .then(response => {
        this.props.onClose();
        Toast.show({
          icon: "thumbs-up",
          message: "Service type created successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to create service type!",
          intent: Intent.DANGER
        });
      });
  }

  updateServiceType() {
    let {
      props: {
        serviceTypeStore: {
          serviceTypeDetail: { 
            object,
            isLoading
           }
        },
        service_type_object,
        service_type_id,
        preferenceStore: {
          tenant
        }
      }
    } = this;
    
    //this.props.serviceTypeStore.serviceTypeDetail.isLoading = true;
    if(service_type_id){
      object.tenant_id = service_type_object.tenant_id;
    }else{
      object.tenant_id = tenant.object.attributes.id;
    }

    object.hourly_service = this.state.isChecked;

    if(this.selectedServiceTypeCategory != null){
      // Cleared in the picker: no category (this used to throw).
      const chosen = this.selectedServiceTypeCategory[0];
      object.attributes.service_type_category_id = chosen ? chosen.value : null;
    }

    if (!this.validate(object.attributes)) {
      return;
    }
    
    object.attributes.service_type_pricing_parameters_attributes = this.selectedPricingParameter.map((option) => {
      return {
        "pricing_parameter_id": option.value
      }
    })

    object.attributes.service_frequencies_attributes = this.selectedFrequency.map((option) => {
      return {
        "frequency_id": option.value
      }
    })

    if(service_type_id){
      object.id = service_type_id;
      this.props.serviceTypeStore.updateServiceType(object)
        .then(this.onSuccessfulUpdateServiceType.bind(this))
        .catch(this.onFailUpdateServiceType.bind(this))
    }
  }

  onSuccessfulUpdateServiceType(response) {
    this.props.onClose();
    Toast.show({ icon: 'thumbs-up', message: 'Service type updated successfully.', intent: Intent.SUCCESS })
  }

  onFailUpdateServiceType(error) {
    Toast.show({ icon: 'warning-sign', message: 'Problem in updating service type!', intent: Intent.DANGER })
  }

  handleCloseServiceTypeDailog() {
    this.props.onClose();
  }

  handleSelectCancel() {
    this.isSelectDialogVisible = false;
    this.showLoader = false;
  }

  toggleChange = () => {
    this.setState({
      isChecked: !this.state.isChecked,
    });
  }

  onServiceTypeSelect(selectedOptions, allOptions) {
    this.selectedPricingParameter = selectedOptions;
  }

  onFrequencySelect(selectedOptions, allOptions) { 
    this.selectedFrequency = selectedOptions;
  }

  onCategorySelect(selectedOptions, allOptions) {
    this.selectedServiceTypeCategory = selectedOptions;
    this.clearError('category');
  }

  
  handleChangeImage = (event) => {
    let { serviceTypeStore, service_type_id } = this.props;
    let object = service_type_id ? serviceTypeStore.serviceTypeDetail.object : serviceTypeStore.newServiceType.object;
  
    var file = event.target.files[0];
    var reader = new FileReader();
    let base64String = '';
    
    reader.onload = function() {
      base64String = reader.result;
      if(service_type_id){
        object.attributes.icon_file = base64String;
        object.attributes.logo = base64String;
      }else{
        object.logo = base64String;
        object.icon_file = base64String;
      }
    };
    reader.readAsDataURL(file);
  };
 
  render() {
    let {
      serviceTypeStore: {
        newServiceType: {
          object,
          isLoading
        }
      },
      service_type_object,
      service_type_id
    } = this.props;

    const isEdit = !!service_type_object;
    if(isEdit){
      object = service_type_object;
    }
    const busy = !!(isEdit ? this.props.serviceTypeStore.serviceTypeDetail.isLoading : isLoading);
    const close = this.handleCloseServiceTypeDailog.bind(this);
    const errors = this.errors;
    const bookingOpen = !!(
      object.display_on_booking_form
      || object.logo
      || (this.selectedPricingParameter && this.selectedPricingParameter.length)
      || (this.selectedFrequency && this.selectedFrequency.length)
    );

    return (
      <FormDrawer
        title={isEdit ? "Edit service type" : "New service type"}
        icon={isEdit ? "edit" : "tag"}
        size="min(720px, 100vw)"
        isOpen={this.props.isOpen}
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            {
              isEdit ?
              <Button
                intent={Intent.PRIMARY}
                icon="floppy-disk"
                text="Save changes"
                loading={busy}
                onClick={this.updateServiceType.bind(this)}
              />
              :
              <Button
                intent={Intent.PRIMARY}
                icon="add"
                text="Create service type"
                loading={busy}
                onClick={this.createServiceType.bind(this)}
              />
            }
          </>
        }
      >
        <FormSection title="Basics" hint="What the service is, as customers and your team see it.">
          <FormGroup
            className={errors.name ? 'has-error' : undefined}
            label="Name"
            labelFor="service-type-name"
            labelInfo="*"
          >
            <input
              id="service-type-name"
              type="text"
              className="bp3-input bp3-fill"
              placeholder="e.g. Furnace tune-up"
              defaultValue={object.name}
              aria-invalid={!!errors.name}
              onChange={event => {
                object.name = event.target.value;
                this.clearError('name');
              }}
            />
            <FieldError>{errors.name}</FieldError>
          </FormGroup>
          <div className="row">
            <div className="col-8">
              <FormGroup
                className={errors.category ? 'has-error' : undefined}
                label="Category"
                labelInfo={object.display_on_booking_form ? '*' : undefined}
              >
                <div id="service-type-category">
                  <ServiceTypeCategoryPicker
                    selectedOptions={this.selectedServiceTypeCategory}
                    onChange={(event) => {this.onCategorySelect(event)} }
                    maximumSelections={1}
                  />
                </div>
                <FieldError>{errors.category}</FieldError>
              </FormGroup>
            </div>
            <div className="col-4">
              <FormGroup label="Color" labelFor="service-type-color">
                <input
                  id="service-type-color"
                  type="color"
                  className="bp3-input bp3-fill"
                  defaultValue={object.color || '#000000'}
                  onChange={event => {
                    object.color = event.target.value;
                  }}
                />
              </FormGroup>
            </div>
          </div>
          <FormGroup
            className={errors.description ? 'has-error' : undefined}
            label="Description"
            labelFor="service-type-description"
            labelInfo="*"
          >
            <TextArea
              id="service-type-description"
              fill={true}
              placeholder="e.g. Clean and inspect the furnace, replace the filter, check for gas leaks."
              defaultValue={object.description}
              aria-invalid={!!errors.description}
              onChange={event => {
                object.description = event.target.value;
                this.clearError('description');
              }}
            />
            <FieldError>{errors.description}</FieldError>
          </FormGroup>
        </FormSection>

        <FormSection title="Pricing" hint="A flat price, or priced by the hour.">
          <div className="row">
            <div className="col-6">
              <FormGroup
                className={errors.rate ? 'has-error' : undefined}
                label="Price"
                labelFor="service-type-price"
                labelInfo="*"
              >
                <input
                  id="service-type-price"
                  type="number"
                  step="any"
                  inputMode="decimal"
                  className="bp3-input bp3-fill"
                  placeholder="e.g. 129.00"
                  defaultValue={ object.rate }
                  aria-invalid={!!errors.rate}
                  onChange={event => {
                    object.rate = event.target.value;
                    this.clearError('rate');
                  }}
                />
                <FieldError>{errors.rate}</FieldError>
              </FormGroup>
            </div>
          </div>
          <FormGroup>
            <Switch
              label="Hourly service"
              checked={!!this.state.isChecked}
              onChange={this.toggleChange}
            />
          </FormGroup>
          {
            this.state.isChecked ?
            <div>
              <FormGroup>
                <Switch
                  label="Allow 30 minute increments"
                  defaultChecked={!!object.allow_thirty_minutes_increments}
                  onChange={event => {
                    object.allow_thirty_minutes_increments = event.target.value;
                  }}
                />
              </FormGroup>
              <div className="row">
                <div className="col-6">
                  <FormGroup label="Hours, from" labelFor="service-type-hours-start">
                    <input
                      id="service-type-hours-start"
                      type='number'
                      className="bp3-input bp3-fill"
                      placeholder="e.g. 1"
                      defaultValue={object.number_of_hours_start}
                      onChange={event => {
                        object.number_of_hours_start = event.target.value;
                      }}
                    />
                  </FormGroup>
                </div>
                <div className="col-6">
                  <FormGroup label="Hours, up to" labelFor="service-type-hours-end">
                    <input
                      id="service-type-hours-end"
                      type='number'
                      className="bp3-input bp3-fill"
                      placeholder="e.g. 8"
                      defaultValue={object.number_of_hours_end}
                      onChange={event => {
                        object.number_of_hours_end = event.target.value;
                      }}
                    />
                  </FormGroup>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <FormGroup label="Technicians, from" labelFor="service-type-techs-start">
                    <input
                      id="service-type-techs-start"
                      type='number'
                      className="bp3-input bp3-fill"
                      placeholder="e.g. 1"
                      defaultValue={object.number_of_technicians_start}
                      onChange={event => {
                        object.number_of_technicians_start = event.target.value;
                      }}
                    />
                  </FormGroup>
                </div>
                <div className="col-6">
                  <FormGroup label="Technicians, up to" labelFor="service-type-techs-end">
                    <input
                      id="service-type-techs-end"
                      type='number'
                      className="bp3-input bp3-fill"
                      placeholder="e.g. 3"
                      defaultValue={object.number_of_technicians_end}
                      onChange={event => {
                        object.number_of_technicians_end = event.target.value;
                      }}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
            : null
          }
        </FormSection>

        <MoreOptions label="Online booking" count={4} open={bookingOpen}>
          <FormGroup helperText="Customers can pick this service when they book online. Needs a category.">
            <Switch
              label="Show on booking form"
              defaultChecked={!!object.display_on_booking_form}
              onChange={(event) => {object.display_on_booking_form = event.target.checked; this.clearError('category');}}
            />
          </FormGroup>
          <FormGroup label="Pricing parameters">
            <PricingParameterPicker
              selectedOptions={this.selectedPricingParameter}
              onChange={(event) => {this.onServiceTypeSelect(event)} }
            />
          </FormGroup>
          <FormGroup label="Frequency">
            <FrequencyPicker
              selectedOptions={this.selectedFrequency}
              onChange={(event) => {this.onFrequencySelect(event)} }
            />
          </FormGroup>
          <FormGroup label="Icon">
            <div className="service-image-main">
              <span className="btn btn-light btn-sm btn-file">
                Change icon <input type="file" aria-label="Change icon" onChange={this.handleChangeImage.bind(this)}/>
              </span>
              <div className="service-image-preview">
                <img src={object.logo || '../icon_invoice.svg'} height='40' alt='icon'/>
              </div>
            </div>
          </FormGroup>
        </MoreOptions>
      </FormDrawer>
    );
  }
}

export default AddServiceTypeModal;
